import { LoaderHandler } from './../../../core/domain/models/loader-enabler';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  TranslatePipe,
  TmsTranslateService,
  TMS_TRANSLATE_SERVICE,
} from '@embraceit/angularcore/tms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Select, Store } from '@ngxs/store';
import { BaseState } from '../state/base.state';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { GridHeader, UpdateGridHeadersRequest } from '../../../core/domain';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import {
  DisableLoading,
  EnableLoading,
  UpdateGridHeaders,
} from '../state/base.actions';
import { UpdateGridHeadersUsecase } from '../../../core/usecases';
import { MessageService } from 'primeng/api';
import { BaseRequest } from 'planning-portal-library/src/lib/data/shared/base.request';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
@Component({
  selector: 'planning-portal-configurations',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    CdkDropList,
    CdkDrag,
    ToggleSwitchModule,
    CdkScrollable,
    ButtonModule,
    InputTextModule
  ],
  templateUrl: './configurations.component.html',
  styles: [],
})
export class ConfigurationsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  subscriptions: Subscription[] = [];
  @Select(BaseState.IsConfigurationUpdated)
  isConfigurationUpdated$?: Observable<boolean>;
  configurationsFormGroup!: FormGroup;
  isSubmitted = false;

  private translate = inject(TMS_TRANSLATE_SERVICE);

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public messageService: MessageService,
    private updateGridHeadersUsecase: UpdateGridHeadersUsecase
  ) {}

  get configurations(): FormArray {
    return this.configurationsFormGroup.controls['configurations'] as FormArray;
  }

  ngOnInit() {
    this.initForm();
    const isConfigurationUpdatedSub = this.isConfigurationUpdated$?.subscribe(
      (res) => {
        if (res !== undefined) {
          const loaderHandler: LoaderHandler = {
            componentName: 'master_plans.common_messages.component_names.configuration_component',
            endPointName: 'master_plans.common_messages.component_names.get_grid_headers',
          };
          this.disableLoading(loaderHandler);
          this.close(res);
        }
      }
    );
    this.subscriptions.push(isConfigurationUpdatedSub!);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.config!.header = this.translate.instant('configurations.page.title');
    }, 0);
  }

  enableLoading(payload: LoaderHandler) {
    this.store.dispatch(new EnableLoading(payload));
  }

  disableLoading(payload: LoaderHandler) {
    this.store.dispatch(new DisableLoading(payload));
  }

  initForm() {
    this.configurationsFormGroup = this.fb.group({
      configurations: this.fb.array([]),
    });

    const gridHeaders: GridHeader[] | undefined = this.store.selectSnapshot(
      BaseState.GridHeaders
    );
    gridHeaders
      ?.sort((x) => x.displayOrder)
      ?.forEach((gridHeader) => this.addConfigurationFormGroup(gridHeader));
  }

  addConfigurationFormGroup(gridHeader?: GridHeader) {
    const formGroup = this.prepareConfigurationFormGroup();
    if (gridHeader) {
      formGroup.controls['id'].patchValue(gridHeader.id);
      formGroup.controls['schemaName'].patchValue(gridHeader.schemaName);
      formGroup.controls['schemaName'].disable();
      formGroup.controls['displayName'].patchValue(gridHeader.displayName);
      formGroup.controls['isShown'].patchValue(gridHeader.isShown);
      // formGroup.controls['isFilterable'].patchValue(gridHeader.isFilterable);
      formGroup.controls['isSortable'].patchValue(gridHeader.isSortable);
      formGroup.controls['displayOrder'].patchValue(gridHeader.displayOrder);
      if (gridHeader.readOnly) {
        // formGroup.controls['isFilterable'].disable();
        formGroup.controls['isSortable'].disable();
      }
    }
    this.configurations.push(formGroup);
  }

  prepareConfigurationFormGroup(): FormGroup {
    return this.fb.group({
      id: [null, [Validators.required]],
      schemaName: [null, [Validators.maxLength(256)]],
      displayName: [null, [Validators.required]],
      isShown: [null, [Validators.required]],
      // isFilterable: [null, [Validators.required]],
      isSortable: [null, [Validators.required]],
      displayOrder: [null, [Validators.required]],
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    const dir = event.currentIndex > event.previousIndex ? 1 : -1;
    const from = event.previousIndex;
    const to = event.currentIndex;
    const temp = this.configurations.at(from);
    for (let i = from; i * dir < to * dir; i = i + dir) {
      const current = this.configurations.at(i + dir);
      this.configurations.setControl(i, current);
    }
    this.configurations.setControl(to, temp);
    this.updateDisplayOrder(event.previousIndex, event.currentIndex);
  }

  updateDisplayOrder(previousIndex: number, currentIndex: number) {
    (this.configurations.at(previousIndex) as FormGroup).controls[
      'displayOrder'
    ].patchValue(previousIndex + 1);
    (this.configurations.at(currentIndex) as FormGroup).controls[
      'displayOrder'
    ].patchValue(currentIndex + 1);
    this.configurationsFormGroup.markAsDirty();
  }

  configValidityCheck(): boolean {
    const configurations = this.configurationsFormGroup.value
      .configurations as UpdateGridHeadersRequest[];
    const even = (element: UpdateGridHeadersRequest) =>
      element.isShown === true;
    const configurationsCheck = configurations?.some(even);
    if (!configurationsCheck) {
      return true;
    }
    return false;
  }

  async onSubmit() {
    this.isSubmitted = true;
    const loaderHandler: LoaderHandler = {
      componentName: this.translate.instant('master_plans.common_messages.component_names.configuration_component'),
      endPointName: this.translate.instant('master_plans.common_messages.component_names.update_grid_headers'),
    };
    this.enableLoading(loaderHandler);
    try {
      if (this.configurationsFormGroup.valid) {
        const configurations = this.configurationsFormGroup.value
          .configurations as UpdateGridHeadersRequest[];

        const baseRequest:
          | BaseRequest<UpdateGridHeadersRequest[]>
          | BaseRequest<UpdateGridHeadersRequest> = {
          request: configurations,
        };
        const updateGridConfigurations$ =
          this.updateGridHeadersUsecase.execute(baseRequest);
        const updatedGridConfigurations = await lastValueFrom(
          updateGridConfigurations$,
          { defaultValue: undefined }
        );
        this.store.dispatch(new UpdateGridHeaders(updatedGridConfigurations));
      }
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translate.instant(
          'common.messages.errors.something_went_wrong_try_again'
        ),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }

  onCancel(event: any) {
    if (this.ref) {
      this.close(false);
    }
  }

  close(flag: boolean) {
    this.ref?.close(flag);
  }

  ngOnDestroy() {
    this.ref.close(true);
    this.store.dispatch(new UpdateGridHeaders(undefined));
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
